import { useState } from 'react';
import { useUser } from '../auth/useUser';
import { useAuth0 } from '@auth0/auth0-react';

export default function AdminPage() {
    const { getAccessTokenSilently } = useAuth0();
    const [userId, setUserId] = useState('');
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Edit states
    const [credits, setCredits] = useState(0);
    const [subTier, setSubTier] = useState('');
    const [subExpires, setSubExpires] = useState('');

    const fetchUser = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setUser(null);

        try {
            const token = await getAccessTokenSilently();
            const res = await fetch(`/api/v1/admin/users/${userId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!res.ok) throw new Error('User not found');

            const data = await res.json();
            setUser(data);
            setCredits(data.credits);
            setSubTier(data.subscriptionTier || '');
            setSubExpires(data.subscriptionExpiresAt || '');
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const updateCredits = async () => {
        try {
            setLoading(true);
            const token = await getAccessTokenSilently();
            const res = await fetch(`/api/v1/admin/users/${user.id}/credits`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ credits: parseInt(credits) })
            });

            if (!res.ok) throw new Error('Failed to update credits');

            const data = await res.json();
            setUser(data);
            setSuccess('Credits updated successfully');
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const updateSubscription = async () => {
        try {
            setLoading(true);
            const token = await getAccessTokenSilently();
            const res = await fetch(`/api/v1/admin/users/${user.id}/subscription`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    tier: subTier,
                    expiresAt: subExpires ? new Date(subExpires).toISOString() : null
                })
            });

            if (!res.ok) throw new Error('Failed to update subscription');

            const data = await res.json();
            setUser(data);
            setSuccess('Subscription updated successfully');
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container mx-auto px-4 py-8">
            <h1 className="text-3xl font-bold mb-8">Admin Dashboard</h1>

            {/* Lookup */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow mb-8">
                <h2 className="text-xl font-semibold mb-4">Find User</h2>
                <form onSubmit={fetchUser} className="flex gap-4">
                    <input
                        type="text"
                        value={userId}
                        onChange={(e) => setUserId(e.target.value)}
                        placeholder="User UUID"
                        className="flex-1 p-2 border rounded dark:bg-gray-700 dark:border-gray-600"
                    />
                    <button
                        type="submit"
                        disabled={loading}
                        className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
                    >
                        Search
                    </button>
                </form>
                {error && <p className="text-red-500 mt-2">{error}</p>}
            </div>

            {user && (
                <div className="grid md:grid-cols-2 gap-8">
                    {/* User Info */}
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                        <h2 className="text-xl font-semibold mb-4">User Details</h2>
                        <div className="space-y-2">
                            <p><span className="font-medium">ID:</span> {user.id}</p>
                            <p><span className="font-medium">Name:</span> {user.name}</p>
                            <p><span className="font-medium">Email:</span> {user.email}</p>
                            <p><span className="font-medium">Created:</span> {new Date(user.createdAt).toLocaleString()}</p>
                        </div>
                    </div>

                    {/* Management */}
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                        <h2 className="text-xl font-semibold mb-4">Manage</h2>

                        {success && <p className="text-green-500 mb-4">{success}</p>}

                        <div className="space-y-6">
                            {/* Credits */}
                            <div>
                                <label className="block text-sm font-medium mb-1">Credits</label>
                                <div className="flex gap-2">
                                    <input
                                        type="number"
                                        value={credits}
                                        onChange={(e) => setCredits(e.target.value)}
                                        className="flex-1 p-2 border rounded dark:bg-gray-700 dark:border-gray-600"
                                    />
                                    <button
                                        onClick={updateCredits}
                                        disabled={loading}
                                        className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                                    >
                                        Update
                                    </button>
                                </div>
                            </div>

                            {/* Subscription */}
                            <div>
                                <label className="block text-sm font-medium mb-1">Subscription Tier</label>
                                <input
                                    type="text"
                                    value={subTier}
                                    onChange={(e) => setSubTier(e.target.value)}
                                    className="w-full p-2 border rounded dark:bg-gray-700 dark:border-gray-600 mb-2"
                                    placeholder="e.g. pro"
                                />

                                <label className="block text-sm font-medium mb-1">Expires At</label>
                                <input
                                    type="datetime-local"
                                    value={subExpires ? new Date(subExpires).toISOString().slice(0, 16) : ''}
                                    onChange={(e) => setSubExpires(e.target.value)}
                                    className="w-full p-2 border rounded dark:bg-gray-700 dark:border-gray-600 mb-2"
                                />

                                <button
                                    onClick={updateSubscription}
                                    disabled={loading}
                                    className="w-full bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700"
                                >
                                    Update Subscription
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
